import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';
import { CheckCircle, WarningAmber } from '@mui/icons-material';
import SnapshotsPanel from './SnapshotsPanel';

// Built-in facts available to rating-table segment expressions. Reference-only:
// mirrors the backend fact surface (server-computed cohort facts, quote refs).
const BUILT_IN_FACTS = [
  ['dependant.age', 'Per-life', 'Always available, built in'],
  ['dependant.relationship', 'Per-life', 'Always available, built in'],
  [
    '_count_<Relationship>',
    'Cohort',
    'Server-computed count per relationship (all values incl. Extended), always present, init 0'
  ],
  ['_any_child_in_education', 'Cohort', 'Server-computed, non-forgeable'],
  ['_all_children_in_education', 'Cohort', 'Server-computed, non-forgeable'],
  ['_any_spouse_disabled', 'Cohort', 'Server-computed, non-forgeable'],
  [
    'quote.* custom fact refs',
    'Quote-level',
    'e.g. quote.loan_amount_in_cents — used by base_value_ref / base_formula on rating nodes'
  ]
];

const TH = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB'
};

const FactsGovernanceTab = (props) => {
  const product = props.product || {};
  const flagged = Boolean(product.requires_manual_review);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Pricing logic lives entirely in <strong>Rating Nodes &amp; Tables</strong>{' '}
        (Packages &amp; Add-ons tabs). This tab is reference-only: the facts a
        rating-table segment expression may reference, and this product&apos;s
        live governance status.
      </Alert>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Built-in Facts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Available to rating-table segment expressions
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Fact</TableCell>
                  <TableCell sx={TH}>Scope</TableCell>
                  <TableCell sx={TH}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {BUILT_IN_FACTS.map((f) => (
                  <TableRow key={f[0]}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                      {f[0]}
                    </TableCell>
                    <TableCell sx={{ color: '#6B7280' }}>{f[1]}</TableCell>
                    <TableCell sx={{ color: '#6B7280' }}>{f[2]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 3 }}>
        Any client-supplied fact key starting with <code>_</code> is rejected at
        the API boundary (reserved namespace) — it cannot be spoofed from a quote
        request.
      </Alert>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937', mb: 1.5 }}>
            Manual Review Status
          </Typography>
          <Chip
            icon={flagged ? <WarningAmber /> : <CheckCircle />}
            label={'requires_manual_review: ' + String(flagged)}
            sx={{
              fontWeight: 600,
              bgcolor: flagged ? '#FFF7ED' : '#F0FDF4',
              color: flagged ? '#9A3412' : '#15803D',
              border: flagged ? '1px solid #FED7AA' : '1px solid #BBF7D0',
              '& .MuiChip-icon': { color: flagged ? '#C2410C' : '#15803D' }
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {flagged
              ? 'Server-owned flag — ratchets to true automatically (e.g. member-chosen bands, count-group overrides, or a rating node with no base). Advisory only; it does not block saving or quoting.'
              : 'No triggers detected. This is a server-owned flag; it is advisory only and does not block saving or quoting.'}
          </Typography>
        </CardContent>
      </Card>

      {product.guid && <SnapshotsPanel productGuid={product.guid} />}

      {props.auditSlot && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937', mb: 1.5 }}>
              Audit History
            </Typography>
            {props.auditSlot}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FactsGovernanceTab;
